// src/pages/ProfilePage.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function ProfilePage() {
  useAuth(); // jen aby byl context inicializovaný, když to někde spoléhá
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [data, setData] = useState({
    username: "",
    email: "",
    role: "",
    pharmacyCode: "",
    fullName: "",
    firma: "",
    phone: "",
    address: { street: "", city: "", zip: "" },
    carrierCustomerNumber: "",
    openingHours: {},
  });

  const normalizeUser = (u = {}) => ({
    username: u.username ?? "",
    email: u.email ?? "",
    role: u.role ?? "user",
    pharmacyCode: u.pharmacyCode ?? "",
    fullName: u.fullName ?? "",
    firma: u.firma ?? "",
    phone: u.phone ?? "",
    address: {
      street: u.address?.street ?? "",
      city: u.address?.city ?? "",
      zip: u.address?.zip ?? "",
    },
    carrierCustomerNumber: u.carrierCustomerNumber ?? "",
    openingHours: u.openingHours ?? {},
  });

  // načti profil z backendu
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/users/me", {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) throw new Error("Chyba při načítání profilu");

        const json = await res.json();
        setData(normalizeUser(json.user));
      } catch (e) {
        setError("Nepodařilo se načíst profil.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // jen pro "user" chceme zobrazovat pobočková pole (adresa, zákaznické číslo, otevírací doba)
  const showBranchFields = data.role === "user";

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const key = name.split(".")[1];
      setData((prev) => ({
        ...prev,
        address: { ...prev.address, [key]: value },
      }));
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // změna otevírací doby
  const onOpeningHoursChange = (day, field, value) => {
    setData((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours?.[day],
          [field]: value,
        },
      },
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setOk("");

    // ==== VALIDACE OPENING HOURS (jen pro user) ====
    if (showBranchFields && data.openingHours) {
      for (const day of Object.keys(data.openingHours)) {
        const d = data.openingHours[day];
        if (!d) continue;

        const open = d.open;
        const close = d.close;

        // prázdné → zavřeno
        if (!open && !close) continue;

        // pokud je vyplněn jen jeden z časů
        if (!open || !close) {
          setError(
            `Den ${day}: musí být vyplněno OPEN i CLOSE, nebo obě pole prázdná.`
          );
          return;
        }

        // převod na minuty
        const toMinutes = (t) => {
          const [h, m] = t.split(":").map(Number);
          return h * 60 + m;
        };

        const openM = toMinutes(open);
        const closeM = toMinutes(close);

        if (closeM <= openM) {
          setError(`Den ${day}: Zavírací čas musí být později než otevírací.`);
          return;
        }
      }
    }

    setSaving(true);

    try {
      // payload skládáme podle role
      const payload = {
        phone: data.phone,
      };

      if (showBranchFields) {
        payload.address = data.address;
        payload.carrierCustomerNumber = data.carrierCustomerNumber;
        payload.openingHours = data.openingHours;
      }

      const res = await fetch("/api/users/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Chyba při ukládání");

      const json = await res.json();
      setData(normalizeUser(json.user));
      setOk("Změny uloženy.");
    } catch (e) {
      setError("Uložení se nezdařilo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Načítám profil…</div>;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Profil</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">{error}</div>
      )}
      {ok && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded">{ok}</div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        {/* read-only sekce */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Uživatelské jméno
            </label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.username}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">E-mail</label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.email}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Role</label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.role}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Kód pobočky</label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.pharmacyCode}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Celé jméno</label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.fullName}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Firma</label>
            <input
              className="w-full border rounded px-3 py-2 bg-gray-100"
              value={data.firma}
              readOnly
            />
          </div>
        </div>

        {/* Telefon (nechávám pro všechny role) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Telefon</label>
            <input
              name="phone"
              value={data.phone}
              onChange={onChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Zákaznické číslo přepravce jen pro user */}
          {showBranchFields ? (
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Zákaznické číslo přepravce
              </label>
              <input
                name="carrierCustomerNumber"
                value={data.carrierCustomerNumber}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          ) : (
            <div className="hidden md:block" />
          )}
        </div>

        {/* Adresa jen pro user */}
        {showBranchFields && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Ulice</label>
              <input
                name="address.street"
                value={data.address.street}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Město</label>
              <input
                name="address.city"
                value={data.address.city}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">PSČ</label>
              <input
                name="address.zip"
                value={data.address.zip}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>
        )}

        {/* Otevírací doba jen pro user */}
        {showBranchFields && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-2">Otevírací doba</h2>

            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2 w-32">Den</th>
                  <th className="border p-2">Otevřeno</th>
                  <th className="border p-2">Zavřeno</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["1", "Pondělí"],
                  ["2", "Úterý"],
                  ["3", "Středa"],
                  ["4", "Čtvrtek"],
                  ["5", "Pátek"],
                  ["6", "Sobota"],
                  ["7", "Neděle"],
                ].map(([key, label]) => (
                  <tr key={key}>
                    <td className="border p-2">{label}</td>

                    <td className="border p-2">
                      <input
                        type="time"
                        className="border rounded px-2 py-1 w-full"
                        value={data.openingHours?.[key]?.open || ""}
                        onChange={(e) =>
                          onOpeningHoursChange(key, "open", e.target.value)
                        }
                      />
                    </td>

                    <td className="border p-2">
                      <input
                        type="time"
                        className="border rounded px-2 py-1 w-full"
                        value={data.openingHours?.[key]?.close || ""}
                        onChange={(e) =>
                          onOpeningHoursChange(key, "close", e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Ukládám…" : "Uložit změny"}
        </button>
      </form>
    </div>
  );
}